import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import {
  FileText,
  User,
  Car,
  Package,
  ReceiptText,
  Search as SearchIcon,
} from "lucide-react";

const TYPE_META = {
  job_card: { label: "Job Cards", Icon: FileText },
  customer: { label: "Customers", Icon: User },
  vehicle: { label: "Vehicles", Icon: Car },
  part: { label: "Parts", Icon: Package },
  invoice: { label: "Invoices", Icon: ReceiptText },
};

export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const groups = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});

  const go = (url) => {
    onOpenChange(false);
    nav(url);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <Command shouldFilter={false}>
        <CommandInput
          data-testid="global-search-input"
          placeholder="Search job cards, customers, vehicles, parts, invoices…"
          value={q}
          onValueChange={setQ}
        />
        <CommandList>
          {!q.trim() && (
            <div className="py-10 text-center text-sm text-[#667085]">
              <SearchIcon size={22} className="mx-auto mb-2 text-[#a8dadc]" />
              Start typing to search across your workshop.
              <div className="mt-2 text-xs">
                <kbd className="px-1.5 py-0.5 bg-[#F7F9FC] border border-[#E4E7EC] rounded font-mono">
                  Ctrl
                </kbd>{" "}
                +{" "}
                <kbd className="px-1.5 py-0.5 bg-[#F7F9FC] border border-[#E4E7EC] rounded font-mono">
                  K
                </kbd>{" "}
                to open anywhere
              </div>
            </div>
          )}
          {q.trim() && !loading && results.length === 0 && (
            <CommandEmpty>No results for &quot;{q}&quot;.</CommandEmpty>
          )}
          {Object.keys(TYPE_META).map((k, idx) =>
            groups[k] ? (
              <div key={k}>
                {idx > 0 && <CommandSeparator />}
                <CommandGroup heading={TYPE_META[k].label}>
                  {groups[k].map((r) => {
                    const { Icon } = TYPE_META[k];
                    return (
                      <CommandItem
                        key={`${k}-${r.id}`}
                        data-testid={`search-result-${k}-${r.id}`}
                        onSelect={() => go(r.url)}
                        className="cursor-pointer"
                      >
                        <Icon size={16} className="mr-2 text-[#457b9d]" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#1d3557] text-[13px] truncate">
                            {r.title}
                          </div>
                          <div className="text-[11px] text-[#667085] truncate">
                            {r.subtitle}
                          </div>
                        </div>
                        {r.meta && (
                          <span className="text-[10px] uppercase tracking-wider text-[#667085] font-semibold">
                            {r.meta}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </div>
            ) : null,
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
